import Vue from 'vue';
import { assign } from 'lodash';
import axios from 'axios';
const createCancelToken = () => axios.CancelToken.source();

const aisRequest = (aisAxios, search) => {
  return aisAxios.get(`search/${encodeURIComponent(search)}`,
    {
      params: {
        include_units: true,
        opa_only: true,
        gatekeeperKey: process.env.VUE_APP_GATE_KEEPER_KEY || '',
      },
    },
  );
};

const state = ({
  selectedProperty: { loading: false },
  eligibilityCheckDetails: {},
  inspections: {
    uuid: null, // Eligibility UUID.
    data: [],
  },
  allInspectionsNames: [],
  inspectionStatusHistory: {},
  foundProperties: [],
  submissions: [],
  successDate: null,

  foundLicenseData: null,
  cancelToken: null,
});

const getters = {
  unitTypeAndNumber: (state) => {
    if (state.selectedProperty.unit_type && state.selectedProperty.unit_num) {
      return `${state.selectedProperty.unit_type} ${state.selectedProperty.unit_num}`;
    }
    return false;
  },
  fixedStreetAddress: (state, getters) =>{
    if (getters.unitTypeAndNumber) {
      return `${String(state.selectedProperty.street_address)
        .replace(getters.unitTypeAndNumber, '')}, ${getters.unitTypeAndNumber}`;
    }
    return String(state.selectedProperty.street_address);
  },
  selectedPropertyAddress: (state, getters)=> {
    if (!state.selectedProperty.opa_account_num) {
      return "";
    }
    return `${getters.fixedStreetAddress},
      ${state.selectedProperty.city},
      ${state.selectedProperty.state}
      ${state.selectedProperty.zip_code}`;
  },
  getRegion: (state, getters, rootState) => (zip) => {
    const humanRedableRegion = [ null, 'First (1)', 'Second (2)', 'Third (3)', 'Fourth (4)' ];
    const result = rootState.zipCodes.find((zipCode) => {
      return zipCode.zipCode === zip;
    });

    if (!result) {
      return {};
    }

    return {
      ...result,
      name: humanRedableRegion[result.regionNumber],
    };
  },
};

const mutations = {
  setSelectedPropertyAddressData(state, property) {
    state.selectedProperty = {
      'opa_account_num': property.opa_account_num,
      'street_address': property.street_address, 
      'unit_type': property.unit_type,
      'unit_num': property.unit_num,
      'city': 'Philadelphia',
      'state': 'Pennsylvania',
      'country': 'USA',
      'zip_code': property.zip_code,
      'opa_owners': property.opa_owners,
      loading: property.loading,
    };
  },
  setFoundProperties(state, found) {
    if (found.length > 0) {
      found.forEach((feature) => {
        state.foundProperties.push(feature.properties);
      });
    } else {
      state.foundProperties = [];
    }
  },
  setSubmissions(state, found) {
    state.submissions = found;
  },
  removeSubmission(state, id) {
    const index = state.submissions.findIndex(s => s.id == id);
    state.submissions.splice(index, 1);
  },
  setEligibilityCheckDetails(state, details) {
    if (!details.inspections) {
      details.inspections = [];
    }
    Vue.set(state, 'eligibilityCheckDetails', details);
  },
  setSuccessDate(state) {
    state.successDate = null;
    state.successDate = this.$dayjs().format('MMMM, DD, YYYY');
  },

  setInspections(state, payload) {

    state.inspections.data = [];
    if (!(Array.isArray(payload.data.nonDraftInspections)
      && payload.data.nonDraftInspections.length)) {
      state.inspections.data = payload.data.draftInspections || [];
    } else {
      payload.data.nonDraftInspections.forEach(inspection => {
        const hasDraftIndex = payload.data.draftInspections.findIndex(ins => ins.name === inspection.name);
        if (hasDraftIndex > -1) {
          inspection.draft = payload.data.draftInspections.splice(hasDraftIndex, 1)[0];
        }
        state.inspections.data.push(inspection);
      });

      if (payload.data.draftInspections.length) {
        // We push any leftover draft inspections in the array to the main list.
        [].push.apply(state.inspections.data, payload.data.draftInspections);
      }
    }
  
    state.inspections.uuid = payload.uuid;
  },

  attachCertificateToInspection(state, payload) {
    const index = state.inspections.data.findIndex(insp => insp.id === payload.inspection);
    state.inspections.data[index].certificates.push(payload.data);
  },

  updateInspectionStatus(state, payload) {
    state.inspections.data.forEach(insp => {
      if (insp.id === payload.inspectionId) {
        insp.status = payload.data.status;

        if (!insp.statusHistory) {
          insp.statusHistory = [];
        }
        insp.statusHistory.push({
          statusReason: payload.data.statusReason,
          statusComment: payload.data.statusComment,
          status: payload.data.status,
          date: this.$dayjs().format(),
          user: payload.user,
        });
      }
    });
  },

  setInspectionStatusHistory(state, payload) {
    if (!state.inspectionStatusHistory[payload.inspectionId]) {
      state.inspectionStatusHistory[payload.inspectionId] = [];
    }

    state.inspectionStatusHistory[payload.inspectionId] = payload.data;
  },

  updateASingleInspection(state, inspection) {
    const index = state.inspections.data.findIndex(insp => insp.id === inspection.id);
    state.inspections.data[index] = assign(state.inspections.data[index], inspection);
  },

  setFoundLicenseData(state, foundLicenseData) {
    state.foundLicenseData = foundLicenseData;
  },
  
  // Here it is a compilation of inspections already created
  // and the new ones names, this is to validate inspection name unique rule.
  allInspectionsNames(state, inspections) {
    console.log("On screen inspections", inspections);

    // Get inspections from the server pool of inspections, drafts or non-drafts.
    let serverOnes = state.inspections.data.filter(insp => {
      if ((insp.status !== 'Draft')
        || ((insp.status === 'Draft')
          && !inspections.some(i => i.id === insp.id))) {
        return true;
      }
      return false;
    }).map(i => i.name);

    // Get inspections being displayed in the screen, removing the server ones in background
    let inScreenAndNew = inspections.filter(insp => {
      if (((!insp.status || insp.status === 'Draft')
        && !insp.fixingInspection)
      || !insp.id) { 
        return true;
      }
      return false;
    }).map(i => i.name);

    state.allInspectionsNames = [].concat(serverOnes, inScreenAndNew);
  },

  setCancelToken(state, token) {
    state.cancelToken = token;
  },
};

const actions = {
  async prior1978(ctx, opaAccountNumber) {
    try {
      const query = `SELECT year_built, year_built_estimate FROM opa_properties_public WHERE parcel_number IN('${opaAccountNumber}')`;
      const response = await this.$cartoAxios.get('/', { params: { q: query }});
      const yearBuilt = this.$helpers.findDeepProp('data.rows.0.year_built', response);
      return (1978 >= yearBuilt) ? true : false;
    } catch (err) {
      // Silence is golden.
      console.log(err);
      return false;
    }
  },

  async getLicenseInformation({ state, commit }, payload) {
    try {
      let propertyType = (payload.propertyType === 'Daycare') ? 'Child Care Facility' : 'Rental';
      let query = ``;
      if (!payload.opaAccountNumber) {
        query = `SELECT * FROM BUSINESS_LICENSES WHERE licensenum = '${payload.licenseNumber}'
        AND licensetype = '${propertyType}' ORDER BY mostrecentissuedate DESC LIMIT 1 OFFSET 0`;
      } else {
        query = `SELECT * FROM BUSINESS_LICENSES WHERE opa_account_num = '${payload.opaAccountNumber}'
        AND licensetype = '${propertyType}' ORDER BY mostrecentissuedate DESC LIMIT 1 OFFSET 0`;
      }
      
      if (state.cancelToken) {
        state.cancelToken.cancel();
        commit('setCancelToken', null);
      }
      
      // Let's issue a new cancel token
      commit('setCancelToken', createCancelToken());

      const { data } = await this.$cartoAxios.get('/', { params: { q: query }, cancelToken: state.cancelToken.token });
      const found = data.rows[0] || false;
      commit('setFoundLicenseData', found);

      return found;
    } catch (err) {
      if (axios.isCancel(err)) {
        throw Error('Request cancelled!');
      }

      commit('setFoundLicenseData', false);
      return err;
    }
  },

  async setGetSelectedPropertyByOPAAccountNumber({ commit }, opa_account_number) {
    try {
      const response = await aisRequest(this.$aisAxios, opa_account_number);
      commit('setSelectedPropertyAddressData', {
        loading: false,
        ...response.data.features[0].properties,
      });
      return true;
    } catch (err) {
      commit('setSelectedPropertyAddressData', { loading: false });
      if (this.$helpers.findDeepProp('response.status', err) === 404) {
        this.$warning('Please, verify your OPA Account Number, the property was not found in our City Records');
        return false;
      }
      this.$fatalError(err);
      return false;
    }
  },

  async returnAISPropertyDataByOPAAccountNumber(ctx, opaAccountNumber) {
    try {
      const cacheName = `address-${opaAccountNumber}`;
      if (this.$sCache.get(cacheName)) {
        return this.$sCache.get(cacheName);
      }
      const response = await aisRequest(this.$aisAxios, opaAccountNumber);
      if (response.data.features[0].properties) {
        this.$sCache.set(cacheName, response.data.features[0].properties);
        return response.data.features[0].properties;
      }
      return null;
    } catch (err) {
      return null;
    }
  },

  async searchProperty({ commit }, address) {  
    commit('setFoundProperties', []);
    try {
      const response = await aisRequest(this.$aisAxios, address);
      commit('setFoundProperties', response.data.features);
      return true;
    } catch (err) {
      if (this.$helpers.findDeepProp('response.status', err) === 404) {
        return false;
      }
      
      this.$fatalError(err);
      return false;
    } 
  },

  async getOPAAccountNumbers(ctx, address) {
    try {
      const response = await aisRequest(this.$aisAxios, address);
      return response.data.features.map((obj) => {
        return obj.properties.opa_account_num;
      });
    } catch (err) {
      if (this.$helpers.findDeepProp('response.status', err) === 404) {
        return [ 0 ];
      }
      return [ 0 ];
    } 
  },

  async getSubmissions({ commit }) {
    try {
      const response = await this.$axios.get('eligibility-checks');
      if (response.data) {
        response.data = this.$readableDates(response.data, /submittedat/, true);
        commit('setSubmissions', response.data.reverse());
      } else {
        commit('setSubmissions', []);
      }
      return true;
    } catch (err) {
      this.$defaultResponseController(err);
      return false;
    }
  },

  async deleteSubmission(ctx, id) {
    try {
      const { data } = await this.$axios.delete(`eligibility-checks/${id}`);
      if (data.success) {
        return true;
      }
      this.$fatalError();
      return false;
    } catch (err) {
      this.$defaultResponseController(err);
      return null;
    }
  },

  async deleteSubmissionAdmin(ctx, id) {
    try {
      const { data } = await this.$axios.delete(`admin/eligibility-checks/${id}`);
      if (data.success) {
        return true;
      }
      this.$fatalError();
      return false;
    } catch (err) {
      this.$defaultResponseController(err);
      return null;
    }
  },

  async submitElegibilityCheck({ rootGetters }, submitData) {
    try {

      let response = null;
      if (!rootGetters['auth/isAdmin']) {
        response  = await this.$axios.post(
          'eligibility-checks',
          submitData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );
      } else {
        response  = await this.$axios.post(
          'admin/eligibility-checks',
          submitData
        );
      
      }
      const ID = this.$helpers.findDeepProp('data.id', response);
      if (ID) {
        return ID;
      } 
      return '';
      
    }catch (err) {
      this.$defaultResponseController(err);
      return false;
    }
  },

  async getEligibilityCheck({ rootGetters, state, commit, dispatch }, uuid) {
    try {
      if (state.eligibilityCheckDetails.id === uuid && state.selectedPropertyAddress) {
        return true;
      }
      
      let response = {};
      if (rootGetters['auth/isAdmin']) {
        response = await this.$axios.get(`admin/eligibility-checks/${uuid}`);
      } else {
        response = await this.$axios.get(`eligibility-checks/${uuid}`);
      }

      if (response.data) {
        commit('setEligibilityCheckDetails', response.data);
        commit('cases/setEligibilityCheckNOV', { eligibilityCheckId: response.data.id, nov: response.data.nov }, { root: true });
        await dispatch('setGetSelectedPropertyByOPAAccountNumber', response.data.opaAccountNumber);
        commit('setFoundLicenseData', null);
        try {
          await dispatch('getLicenseInformation',
            {
              licenseNumber: response.data.licenseNumber,
              propertyType: response.data.propertyType,
              opaAccountNumber: response.data.opaAccountNumber,
            }
          );
        } catch (err) {
          // Silence is golden.
        }
      } else {
        this.$fatalError();
      }
      return true;
    } catch (err) {
      if (this.$helpers.findDeepProp('response.status', err) === 404) {
        commit('setEligibilityCheckDetails', {});
      } else {
        this.$defaultResponseController(err); 
      }
      return false;
    }
  },

  async getInspections({ rootGetters, state, commit }, payload) {
    if (state.inspections.uuid === payload.uuid && !payload.forceUpdate) {
      return true;
    }

    try {
      let response = {};
      if (rootGetters['auth/isAdmin']) {
        response = await this.$axios.get(`admin/eligibility-check/${payload.uuid}/inspections`);
      } else {
        response = await this.$axios.get(`eligibility-check/${payload.uuid}/inspections`);
      }
      if (response.data) {
        commit('setInspections', { data: response.data, uuid: payload.uuid, isAdmin: rootGetters['auth/isAdmin'] });
      } else {
        this.$fatalError();
      }
      return true;
    } catch (err) {
      this.$defaultResponseController(err);
      return false;
    }
  },

  async getInspectionStatusTimeline({ commit }, submitData) {
    try {
      const { data } = await this.$axios.get(
        `/eligibility-check/${submitData.eligibilityCheckId}/inspections/${submitData.inspectionName}/timeline`
      );

      if (data) {
        commit('setInspectionStatusHistory', {
          inspectionId: submitData.inspectionId,
          data: data.inspectionTimeline,
        });
      } else {
        commit('setInspectionStatusHistory', {
          inspectionId: submitData.inspectionId,
          data: [],
        });
      }
      return true;
    } catch (err) {
      this.$defaultResponseController(err);
      return false;
    }
  },

  async submitInspections({ rootGetters, commit, dispatch }, submitData) {
    try {
      let response = {};
      if (submitData.inspectionId) { // If we have an ID it means this inspection is being updated (PATCH)
        response = await this.$axios.patch(
          `eligibility-check/${submitData.eligibilityCheckUUID}/inspections/${submitData.inspectionId}`,
          submitData,
        );
      } else {
        if (rootGetters['auth/isAdmin']) {
          response = await this.$axios.post(
            `admin/eligibility-check/${submitData.eligibilityCheckUUID}/inspections`,
            submitData,
          );
        } else {
          response = await this.$axios.post(
            `eligibility-check/${submitData.eligibilityCheckUUID}/inspections`,
            submitData,
          );
        }
      }

      await dispatch('getInspections', { uuid: submitData.eligibilityCheckUUID, forceUpdate: true });
      if (response.data.success) {
        if (response.data.isFinished) {
          // Get the submitted inspections.
          commit('setSuccessDate');
          this.$router.push({ name: 'submit-inspections-success', params: { uuid: submitData.eligibilityCheckUUID }});
          return;
        }
  
        this.$success('Data successfully saved');
        this.$router.push({ name: 'submissions-details', params: { uuid: submitData.eligibilityCheckUUID }});

        return true;
      } 
      this.$warning(response.data.message);
      return false;
      
    } catch (err) {
      this.$defaultResponseController(err);
      return false;
    }
  },

  async updateInspection({ commit }, submitData) {
    try {
      const { data } = await this.$axios.patch(
        `admin/eligibility-check/${submitData.eligibilityCheckUUID}/inspections/${submitData.inspection.id}`,
        submitData.inspection,
      );
      if (data) {
        commit('updateASingleInspection', data.inspection);
        return true;
      } 
      this.$fatalError();
      return false;
      
    } catch (err) {
      this.$defaultResponseController(err);
      return false;
    }
  },

  async submitCertificate({ rootGetters, commit }, payload) {
    try {
      // TODO: There must be a better way to do this.
      let response = null;
      if (rootGetters['auth/isAdmin']) {
        response = await this.$axios.post(
          `admin/eligibility-check/${payload.eligibilityCheck}/inspections/${payload.inspection}/certificate`, 
          { certificateAwsFileName: payload.certificateAwsFileName }
        );
      } else {
        response = await this.$axios.post(
          `eligibility-check/${payload.eligibilityCheck}/inspections/${payload.inspection}/certificate`, 
          { certificateAwsFileName: payload.certificateAwsFileName }
        );
      }

      if (response.data) {
        commit('attachCertificateToInspection', { inspection: payload.inspection, data: response.data });
        return true;
      } 
      return false;
      
    } catch (err) {
      this.$defaultResponseController(err);
      return false;
    }
  },

  // Admin
  async getEligibilityChecks(ctx, payload) {
    try {
      const { data } = await this.$axios.get('admin/eligibility-checks/search', { params: payload });
      if (data) {        
        return data;
      } 
      return [];
      
    } catch (err) {
      return [];
    }
  },

  async updateInspectionStatus({ commit }, payload) {
    try {
      const { data } = await this.$axios.put(
        `admin/eligibility-check/${payload.eligibilityCheckUUID}/inspections/${payload.inspectionId}/status`,
        payload.data
      );
      if (data) {
        commit('updateInspectionStatus', payload);
      } else {
        this.$fatalError();
      }

      return true;
    } catch (err) {
      this.$defaultResponseController(err);
      return false;
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};